import { Item } from '@zendeskgarden/react-dropdowns'
import { Checkbox, Hint, Input, Label, Field as ZenField } from '@zendeskgarden/react-forms'
import React, { useCallback, useMemo, useState } from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import GripIcon from '../../icons/grip.svg?react'
import LeftArrow from '../../icons/left-arrow.svg?react'
import PlusIcon from '../../icons/ps_general_plus_dark.svg?react'
import { Field } from '../../models/Field'
import { Option } from '../../models/Option'
import { RecordFactory } from '../../models/RecordFactory'
import { Type } from '../../models/Type'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSButton, PSButtonDanger, PSButtonPrimary } from '../app/PSButton'
import { PSDropdown } from '../app/PSDropdown'
import Paragraph from '../app/design/Paragraph'
import { GuardrailModal } from '../common/modals/GuardrailModal'
import { SelectOption, fieldQuestionTypeOptions } from '../settings/Options'

type UrlParams = {
    fieldID?: string
    formID?: string
}

type Props = {
    editField?: Field
    forOnboarding?: boolean
    forClient?: boolean
    hideBackButton?: boolean
    onSave?: (field: Field) => void
}

// NOTE: this component serves to edit one and only one field at a time. No need to load all of a form's fields when a formID is present
const viewWillAppear = async (base: PocketComponent<Props>) => {
    const urlParams = base.urlParams as UrlParams | undefined

    // if a field ID is present, a form ID will also be present
    if (urlParams?.formID && urlParams?.fieldID) {
        const field = await RecordFactory.getInstance().loadField(urlParams.fieldID)
        return { field, formID: urlParams.formID, fieldID: urlParams.fieldID }
    }

    // when there is a formID but no fieldID, we are creating a new question
    if (urlParams?.formID) {
        return { field: undefined, formID: urlParams.formID, fieldID: undefined }
    }

    // otherwise, we are editing a field from a prop, likely onboarding
    return { field: base.props.editField, formID: undefined, fieldID: undefined }
}

const render = ({ base, data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const navigate = usePSNavigate()

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

    const [label, setLabel] = useState(data.field?.label ?? '')
    const [type, setType] = useState<SelectOption | undefined>(
        fieldQuestionTypeOptions.find((o) => o.value === data.field?.type)
    )
    const [required, setRequired] = useState(data.field?.required ?? true)
    const [showOnline, setShowOnline] = useState(data.field?.isPublic ?? true)
    const [isBooking, setIsBooking] = useState(data.field?.booking ?? false)
    const [choices, setChoices] = useState(
        (data.field?.options.map((o) => o.label) ?? []) as string[]
    )

    const { forClient, forOnboarding, onSave, hideBackButton } = base.props

    const isClient = data.field?.client ?? false
    const showChoices = type?.value === 'select' || type?.value === 'multiselect'
    const saveEnabled = useMemo(() => {
        if (type && (type.value === 'select' || type.value === 'multiselect'))
            return !!label && !!type && choices.length > 0 && !!choices[0].trim()
        return !!label.trim() && !!type
    }, [label, type, choices])

    const addNewChoice = () => {
        const choicesCopy = choices.slice()
        choicesCopy.push('')
        setChoices(choicesCopy)
    }

    const setChoice = (value: string, index: number) => {
        if (choices.length >= index + 1) {
            const choicesCopy = choices.slice()
            choicesCopy[index] = value
            setChoices(choicesCopy)
        }
    }

    const removeChoice = (index: number) => {
        const choicesCopy = choices.slice()
        choicesCopy.splice(index, 1)
        setChoices(choicesCopy)
    }

    const onDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return

            const [removed] = choices.splice(result.source.index, 1)

            choices.splice(result.destination.index, 0, removed)
            setChoices(choices)
        },
        [choices]
    )

    const onSaveQuestion = async () => {
        const newField = data.field?.clone() ?? new Field()

        newField.label = label
        newField.type = type?.value
        newField.required = required
        newField.isPublic = showOnline
        newField.client = isClient || !!forClient
        newField.booking = isBooking
        newField.editionField = data.field?.id

        newField.options = choices.map((choice, index) => {
            const option = new Option()
            option.seqnum = index
            option.label = choice
            return option
        })

        if (data.formID) {
            newField.record = new Type({ id: data.formID })
        }

        // favor the on save prop if exists
        if (onSave) {
            onSave(newField)
            return
        }

        if (data.formID || forClient) {
            base.showProgressBar('Saving...')
            const result = await RecordFactory.getInstance().sendField(newField)
            base.hideProgressBar()

            if (!result.ok) {
                base.logAndDisplayError(result.error)
                return
            }

            navigate(
                data.formID
                    ? `/settings/forms/${data.formID}/questions`
                    : '/settings/leads/questions'
            )
            return
        }

        navigate(-1)
    }

    const onDeleteQuestion = async () => {
        if (!data.field?.id) {
            return
        }

        base.showProgressBar()
        const result = await RecordFactory.getInstance().deleteField(data.field.id)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        if (forClient) {
            navigate('/settings/leads/questions')
            return
        }

        if (data.formID) {
            navigate(`/settings/forms/${data.formID}/questions`)
            return
        }

        navigate(-1)
    }

    return (
        <div className="m-12 flex flex-col gap-8">
            <div className="flex justify-between">
                {!hideBackButton && (
                    <PSButton
                        style={{ marginLeft: 0, marginRight: 16 }}
                        onClick={() => navigate(-1)}
                    >
                        <LeftArrow />
                    </PSButton>
                )}
                <h1>Question builder</h1>
                {data.fieldID && (
                    <PSButtonDanger
                        style={{ height: '40px' }}
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Delete question
                    </PSButtonDanger>
                )}
                <PSButtonPrimary
                    style={{ height: '40px', marginLeft: data.fieldID ? '16px' : 'auto' }}
                    onClick={() => onSaveQuestion()}
                    disabled={!saveEnabled}
                >
                    Save
                </PSButtonPrimary>
            </div>
            <div className="separator" />
            <form className="flex flex-col gap-8">
                <ZenField className="field">
                    <Label className="label">Question (required)</Label>
                    <Input
                        placeholder="Add question"
                        value={label}
                        maxLength={199}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                </ZenField>

                <PSDropdown
                    selected={type}
                    nameProperty="label"
                    disabled={!!data.fieldID || forOnboarding}
                    onSelect={(selection) => setType(selection)}
                    label="Question type (required)"
                    placeholder={<div className="grey">Select question type</div>}
                >
                    <>
                        {fieldQuestionTypeOptions.map((option) => (
                            <Item key={`question-type-${option.value}`} value={option}>
                                {option.label}
                            </Item>
                        ))}
                    </>
                </PSDropdown>

                <ZenField className="field">
                    <Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)}>
                        <Label className="withHint">Required</Label>
                        <Hint>Is this question required on this form?</Hint>
                    </Checkbox>
                </ZenField>

                {forClient && (
                    <ZenField className="field">
                        <Checkbox
                            checked={showOnline}
                            onChange={(e) => setShowOnline(e.target.checked)}
                        >
                            <Label>Show online</Label>
                        </Checkbox>
                    </ZenField>
                )}

                {/*&& !editField*/}
                {forClient && (
                    <ZenField className="field">
                        <Checkbox
                            checked={isBooking}
                            onChange={(e) => setIsBooking(e.target.checked)}
                        >
                            <Label>Show on appointment</Label>
                        </Checkbox>
                    </ZenField>
                )}

                <ZenField className="field">
                    <Checkbox
                        checked={showOnline}
                        onChange={(e) => setShowOnline(e.target.checked)}
                    >
                        <Label className="withHint">Show online</Label>
                        <Hint>Enable if you'd like clients to fill out this question</Hint>
                    </Checkbox>
                </ZenField>

                {showChoices && (
                    <>
                        <div className="separator" />
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(droppableProps: any) => {
                                    return (
                                        <div ref={droppableProps.innerRef}>
                                            {choices.map((choice, index) => {
                                                return (
                                                    <Draggable
                                                        key={`choice-${index}`}
                                                        draggableId={`${choice}-${index}`}
                                                        index={index}
                                                    >
                                                        {(provided: any, snapshot: any) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                            >
                                                                <ZenField className="field">
                                                                    <Label>
                                                                        Choice {index + 1}
                                                                    </Label>
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            flexDirection: 'row',
                                                                        }}
                                                                    >
                                                                        <div style={{ flex: 1 }}>
                                                                            <Input
                                                                                placeholder="Add a choice"
                                                                                maxLength={99}
                                                                                value={choice}
                                                                                onChange={(e) =>
                                                                                    setChoice(
                                                                                        e.target
                                                                                            .value,
                                                                                        index
                                                                                    )
                                                                                }
                                                                            />
                                                                            <div
                                                                                className="count"
                                                                                style={{
                                                                                    color:
                                                                                        99 -
                                                                                            choice.length <
                                                                                        10
                                                                                            ? '#eb5757'
                                                                                            : '#999999',
                                                                                }}
                                                                            >
                                                                                {99 - choice.length}
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                height: 48,
                                                                                paddingTop: 16,
                                                                                paddingLeft: 16,
                                                                                alignSelf: 'center',
                                                                            }}
                                                                        >
                                                                            <GripIcon />
                                                                        </div>
                                                                    </div>
                                                                    {index > 0 && (
                                                                        <div
                                                                            style={{
                                                                                color: '#ff3f3f',
                                                                                fontWeight: 700,
                                                                                cursor: 'pointer',
                                                                                display:
                                                                                    'inline-block',
                                                                            }}
                                                                            onClick={() =>
                                                                                removeChoice(index)
                                                                            }
                                                                        >
                                                                            Remove choice &gt;
                                                                        </div>
                                                                    )}
                                                                </ZenField>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                )
                                            })}
                                        </div>
                                    )
                                }}
                            </Droppable>
                        </DragDropContext>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                margin: '32px 0 64px',
                                cursor: 'pointer',
                            }}
                            onClick={() => addNewChoice()}
                        >
                            <PlusIcon />
                            <span style={{ paddingLeft: 12, fontWeight: 700 }}>
                                Add another choice
                            </span>
                        </div>
                    </>
                )}
            </form>
            {showDeleteConfirmation && (
                <GuardrailModal
                    title="Are you sure?"
                    buttonText="Delete"
                    onClose={() => setShowDeleteConfirmation(false)}
                    onAction={() => onDeleteQuestion()}
                >
                    <Paragraph>Are you sure you'd like to delete this question?</Paragraph>
                </GuardrailModal>
            )}
        </div>
    )
}

export const FieldPage = usePSComponent(render, viewWillAppear)
